/**
 * Die **Titel-Palette** (entschieden 2026-09-25 im Titelfarben-Labor, siehe
 * `.claude/tasks/project-title-icon.md`): 16 Hauptfarben, jede in 11 Stufen
 * (50–950), berechnet in OKLCH. Sie färbt den Projekttitel, die Lucide-Icons
 * und — gerundet — die Auswahlfläche des Projekt-Icons in der Sidebar.
 *
 * Die Hex-Werte stehen als Tokens `--title-<farbe>-<stufe>` in `globals.scss`
 * (`npm run tokens:title`); ein Unit-Test hält beide gleich. Hier stehen sie
 * nur, damit Rechnungen (nächste Farbe zu einem Bild) ohne DOM gehen.
 *
 * Bewusst ohne Importe: `scripts/export-title-palette.mts` lädt die Datei
 * direkt mit Node.
 */

export const TITLE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const
export type TitleStep = (typeof TITLE_STEPS)[number]

/** Standardstufe im Hellmodus (600) und im Dunkelmodus (400) — als Index in {@link TITLE_STEPS}. */
export const TITLE_BASE_INDEX = { light: 6, dark: 4 } as const
/** Varianten je Farbe: Standardstufe ±3. */
export const TITLE_MAX_OFFSET = 3

const LIGHTNESS = [0.975, 0.94, 0.885, 0.81, 0.71, 0.635, 0.565, 0.495, 0.435, 0.385, 0.28]
const CHROMA_CURVE = [0.12, 0.24, 0.45, 0.7, 0.9, 1, 0.97, 0.87, 0.74, 0.62, 0.46]

export interface TitleColorFamily {
  key: string
  /** Farbton in Grad (OKLCH). */
  hue: number
  /** Größte Buntheit, erreicht bei Stufe 500. */
  chroma: number
  /** Wie weit der Ton zur dunkelsten Stufe hin wandert (Gelb wird sonst olivgrün). */
  drift?: number
}

/** In der Reihenfolge, in der der Wähler sie zeigt: erst die Neutralen, dann nach Farbton. */
export const TITLE_COLORS: readonly TitleColorFamily[] = [
  { key: 'gray', hue: 257, chroma: 0.018 },
  { key: 'brown', hue: 58, chroma: 0.075, drift: -8 },
  { key: 'rose', hue: 12, chroma: 0.23 },
  { key: 'red', hue: 25, chroma: 0.24 },
  { key: 'orange', hue: 50, chroma: 0.21, drift: -10 },
  { key: 'yellow', hue: 90, chroma: 0.19, drift: -38 },
  { key: 'lime', hue: 131, chroma: 0.21, drift: -6 },
  { key: 'green', hue: 150, chroma: 0.19 },
  { key: 'emerald', hue: 163, chroma: 0.17 },
  { key: 'teal', hue: 185, chroma: 0.13 },
  { key: 'sky', hue: 237, chroma: 0.16 },
  { key: 'blue', hue: 259, chroma: 0.22 },
  { key: 'indigo', hue: 277, chroma: 0.23 },
  { key: 'violet', hue: 293, chroma: 0.25 },
  { key: 'fuchsia', hue: 322, chroma: 0.27 },
  { key: 'pink', hue: 354, chroma: 0.22 },
]

export type TitleColorKey = string

/**
 * **Schwarz** — die Tinte der App (`--foreground`, dunkel auf hell, hell auf
 * dunkel). In den Wählern steht es an der Stelle von Grau, ohne Abstufungen
 * (gewünscht 2026-09-26). Grau bleibt in der Palette (Tokens, Rundung).
 */
export const TITLE_FOREGROUND = 'foreground'

/**
 * **Weiß** — reines Weiß in beiden Modi (gewünscht 2026-09-26), gedacht für
 * Titel auf einem kräftigen Marker. Nur im Titel: In Sidebar und Tabs bleibt
 * ein weißer Titel in der normalen Farbe, als Icon wäre es auf heller
 * Sidebar unsichtbar.
 */
export const TITLE_WHITE = 'white'

/** Was die Wähler anbieten: Schwarz, dann die bunten und Braun — ohne Grau. */
export const TITLE_PICKER_COLORS: readonly string[] = [
  TITLE_FOREGROUND,
  ...TITLE_COLORS.filter(c => c.key !== 'gray').map(c => c.key),
]

/** Ein gültiger Farbwert eines Projekt-Icons: eine Titel-Farbe oder Schwarz. */
export function isIconColorKey(key: string | null | undefined): key is string {
  return key === TITLE_FOREGROUND || isTitleColorKey(key)
}

/**
 * Der Schlüssel einer Titel-Farbe samt Abstufung, wie er im HTML des Titels
 * steht (`data-color`): `blue` ist die Standardstufe, `blue+1` eine dunkler
 * (im Dunkelmodus: heller), `blue-2` zwei heller.
 */
export function titleColorKey(family: string, offset = 0): string {
  return offset === 0 ? family : `${family}${offset > 0 ? '+' : ''}${offset}`
}

export function parseTitleColorKey(key: string | null | undefined): { family: string; offset: number } | null {
  const match = key?.match(/^([a-z]+)([+-][1-9])?$/)
  if (!match || !isTitleColorKey(match[1])) return null
  const offset = match[2] ? Number(match[2]) : 0
  return Math.abs(offset) <= TITLE_MAX_OFFSET ? { family: match[1], offset } : null
}

/** Der Name der CSS-Variable, die eine Abstufung im Titel auflöst (`ProjectTitle.scss`). */
export function titleInkVar(family: string, offset: number): string {
  const suffix = offset === 0 ? '0' : offset > 0 ? `p${offset}` : `m${-offset}`
  return `--title-ink-${family}-${suffix}`
}

export function isTitleColorKey(key: string | null | undefined): key is TitleColorKey {
  return !!key && TITLE_COLORS.some(c => c.key === key)
}

function oklchToLinear(l: number, c: number, h: number): [number, number, number] {
  const a = c * Math.cos((h * Math.PI) / 180)
  const b = c * Math.sin((h * Math.PI) / 180)
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b
  const s_ = l - 0.0894841775 * a - 1.291485548 * b
  const L = l_ ** 3
  const M = m_ ** 3
  const S = s_ ** 3
  return [
    4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
    -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
    -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
  ]
}

const inGamut = (rgb: number[]) => rgb.every(v => v >= -0.0005 && v <= 1.0005)

function gamma(v: number): number {
  const x = Math.min(1, Math.max(0, v))
  return x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055
}

/** OKLCH → Hex; liegt die Farbe außerhalb von sRGB, sinkt nur die Buntheit. */
export function oklchToHex(l: number, c: number, h: number): string {
  let rgb = oklchToLinear(l, c, h)
  if (!inGamut(rgb)) {
    let lo = 0
    let hi = c
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2
      if (inGamut(oklchToLinear(l, mid, h))) lo = mid
      else hi = mid
    }
    rgb = oklchToLinear(l, lo, h)
  }
  return '#' + rgb.map(v => Math.round(gamma(v) * 255).toString(16).padStart(2, '0')).join('')
}

export function titleShade(family: TitleColorFamily, stepIndex: number): string {
  const hue = family.hue + (family.drift ?? 0) * (stepIndex / 10)
  return oklchToHex(LIGHTNESS[stepIndex], family.chroma * CHROMA_CURVE[stepIndex], hue)
}

/** Alle Tokens als `[name, hex]`, in der Reihenfolge, in der `globals.scss` sie führt. */
export function titlePaletteTokens(): [string, string][] {
  return TITLE_COLORS.flatMap(family =>
    TITLE_STEPS.map((step, i) => [`--title-${family.key}-${step}`, titleShade(family, i)] as [string, string]),
  )
}

// ── Nächste Titel-Farbe zu einem Farbwert (Happy Emojis, Emojis) ──────────

export function srgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const lin = (v: number) => {
    const x = v / 255
    return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
  }
  const R = lin(r)
  const G = lin(g)
  const B = lin(b)
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B)
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B)
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

/** Unter dieser Buntheit gilt ein Bild als grau. */
const NEUTRAL_CHROMA = 0.03
/** Warme Töne bis zu dieser Buntheit sind Braun, nicht Orange oder Gelb. */
const BROWN_CHROMA = 0.085

/**
 * Die Titel-Farbe, deren Ton einem gemessenen Farbwert am nächsten liegt.
 * Verglichen wird nur der Farbton (OKLCH): Die Helligkeit eines Bildes sagt
 * nichts darüber, welche Familie es ist. Blasses landet bei Grau oder Braun.
 */
export function nearestTitleColor(rgb: readonly [number, number, number]): TitleColorKey {
  const [, a, b] = srgbToOklab(rgb[0], rgb[1], rgb[2])
  const chroma = Math.hypot(a, b)
  const hue = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360
  if (chroma < NEUTRAL_CHROMA) return 'gray'
  if (chroma < BROWN_CHROMA && hue > 20 && hue < 110) return 'brown'
  if (chroma < NEUTRAL_CHROMA * 1.5) return 'gray'
  let best = TITLE_COLORS[2]
  let bestDistance = Infinity
  for (const family of TITLE_COLORS) {
    if (family.chroma < 0.1) continue
    const d = Math.min(Math.abs(family.hue - hue), 360 - Math.abs(family.hue - hue))
    if (d < bestDistance) {
      bestDistance = d
      best = family
    }
  }
  return best.key
}
